import React, { FC, Fragment, useState } from 'react';
import { Box, IconButton, Link, TableCell, TableRow, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ProductionOverviewCellInput,
    ProductionOverviewCountMode,
    ProductionOverviewRowKey,
} from 'models/production-overview';
import {
    DAY_CELL_ATTRIBUTE,
    OverviewCalendarGroup,
    OverviewCalendarRow,
    buildRowCellInput,
    getGroupHeaderCount,
    getHeatColor,
    getRowCount,
    getTodayKey,
    isFutureDay,
    isMonday,
    isWeekendDay,
    pickCount,
    TODAY_COLUMN_COLOR,
    WEEKEND_COLUMN_COLOR,
} from './production-overview-utils';

export type OverviewCellClickHandler = (labelParts: string[], cells: ProductionOverviewCellInput[]) => void;

type OverviewGroupRowProps = {
    group: OverviewCalendarGroup;
    days: string[];
    countMode: ProductionOverviewCountMode;
    heatMaxCount: number;
    isCompact: boolean;
    onCellClick: OverviewCellClickHandler;
};

/** Days a row cannot have numbers for yet are hatched, so the forward-looking rows stand out. */
const NOT_YET_PATTERN = 'repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0 1px, transparent 1px 7px)';

const dayCellBorders = (dayKey: string, isCompact: boolean) => ({
    borderLeft: isMonday(dayKey) ? '2px solid #ccc' : undefined,
    paddingX: isCompact ? 0.25 : 0.5,
    paddingY: 0.5,
});

export const dayColumnColor = (dayKey: string): string => {
    if (dayKey === getTodayKey()) return TODAY_COLUMN_COLOR;
    return isWeekendDay(dayKey) ? WEEKEND_COLUMN_COLOR : 'transparent';
};

/** The heat tint is an image layer, so the column colour underneath still shows. */
const cellBackgroundImage = (count: number, heatMaxCount: number, isNotYet?: boolean): string | undefined => {
    if (count > 0) {
        const heatColor = getHeatColor(count, heatMaxCount);
        return `linear-gradient(${heatColor}, ${heatColor})`;
    }
    return isNotYet ? NOT_YET_PATTERN : undefined;
};

type CountCellProps = {
    dayKey: string;
    count: number;
    lateCount?: number;
    heatMaxCount: number;
    isCompact: boolean;
    isHeader?: boolean;
    /** The row only records what has happened, and this day is still ahead. */
    isNotYet?: boolean;
    /** A count that opens another page links there instead of opening the dialog. */
    href?: string;
    onClick?: () => void;
    onLateClick?: () => void;
};

const CountCell: FC<CountCellProps> = ({
    dayKey,
    count,
    lateCount,
    heatMaxCount,
    isCompact,
    isHeader,
    isNotYet,
    href,
    onClick,
    onLateClick,
}: CountCellProps) => {
    const { t: translate } = useTranslation(['production-overview']);
    const isClickable = count > 0 && Boolean(onClick) && !href;

    const countText = (
        <Typography variant="body2" fontWeight={isHeader ? 600 : 500} fontSize={isCompact ? '0.7rem' : undefined}>
            {count}
        </Typography>
    );

    return (
        <TableCell
            {...{ [DAY_CELL_ATTRIBUTE]: true }}
            align="center"
            onClick={isClickable ? onClick : undefined}
            sx={{
                ...dayCellBorders(dayKey, isCompact),
                backgroundColor: dayColumnColor(dayKey),
                backgroundImage: cellBackgroundImage(count, heatMaxCount, isNotYet),
                cursor: isClickable ? 'pointer' : 'default',
                '&:hover': isClickable ? { textDecoration: 'underline' } : undefined,
            }}
        >
            {count > 0 && href ? (
                <Link component={RouterLink} to={href} color="inherit" underline="hover">
                    {countText}
                </Link>
            ) : undefined}
            {count > 0 && !href ? countText : undefined}
            {lateCount ? (
                <Typography
                    component="div"
                    onClick={(event) => {
                        event.stopPropagation();
                        onLateClick?.();
                    }}
                    sx={{
                        fontSize: isCompact ? '0.6rem' : '0.65rem',
                        color: 'error.main',
                        fontWeight: 600,
                        cursor: onLateClick ? 'pointer' : 'default',
                        '&:hover': onLateClick ? { textDecoration: 'underline' } : undefined,
                    }}
                >
                    {translate('production-overview:LATE_COUNT', { count: lateCount })}
                </Typography>
            ) : undefined}
        </TableCell>
    );
};

const OverviewGroupRow: FC<OverviewGroupRowProps> = ({
    group,
    days,
    countMode,
    heatMaxCount,
    isCompact,
    onCellClick,
}: OverviewGroupRowProps) => {
    const { t: translate } = useTranslation(['production-overview']);
    const [isExpanded, setIsExpanded] = useState(true);
    const todayKey = getTodayKey();
    const groupLabel = translate(`production-overview:${group.labelKey}`);

    const labelCellStyle = {
        position: 'sticky' as const,
        left: 0,
        backgroundColor: 'background.paper',
        zIndex: 1,
        whiteSpace: 'nowrap' as const,
    };

    const handleGroupTotalClick = (dayKey: string) => {
        if (group.groupTotalRowKey) {
            onCellClick([groupLabel, dayKey], [{ rowKey: group.groupTotalRowKey, day: dayKey }]);
            return;
        }
        const cells = group.rows
            .filter((row) => !row.pointInTimeOnly || dayKey === todayKey)
            .map((row) => buildRowCellInput(row, dayKey));
        onCellClick([groupLabel, dayKey], cells);
    };

    const handleRowCellClick = (row: OverviewCalendarRow, dayKey: string) => {
        const rowLabel = translate(`production-overview:${row.labelKey}`);
        onCellClick([groupLabel, rowLabel, row.pointInTimeOnly ? todayKey : dayKey], [buildRowCellInput(row, dayKey)]);
    };

    const handleLateTodayClick = (row: OverviewCalendarRow) => {
        const rowLabel = translate(`production-overview:${row.labelKey}`);
        onCellClick(
            [groupLabel, rowLabel, translate('production-overview:LATE')],
            [{ rowKey: ProductionOverviewRowKey.LATE_TODAY }],
        );
    };

    return (
        <Fragment>
            <TableRow data-test={`production-overview.group-${group.groupKey}`}>
                <TableCell sx={labelCellStyle}>
                    <Box display="flex" alignItems="center">
                        {group.rows.length ? (
                            <IconButton
                                size="small"
                                onClick={() => setIsExpanded(!isExpanded)}
                                aria-label={groupLabel}
                                data-test={`production-overview.toggle-group-${group.groupKey}`}
                            >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        ) : undefined}
                        <Typography variant="body2" fontWeight={600}>
                            {groupLabel}
                        </Typography>
                    </Box>
                </TableCell>
                {days.map((dayKey) =>
                    group.showDayTotals ? (
                        <CountCell
                            key={dayKey}
                            dayKey={dayKey}
                            count={getGroupHeaderCount(group, dayKey, countMode)}
                            heatMaxCount={heatMaxCount}
                            isCompact={isCompact}
                            isHeader
                            isNotYet={isFutureDay(dayKey)}
                            href={group.headerHref}
                            onClick={() => handleGroupTotalClick(dayKey)}
                        />
                    ) : (
                        <TableCell
                            key={dayKey}
                            {...{ [DAY_CELL_ATTRIBUTE]: true }}
                            sx={{ ...dayCellBorders(dayKey, isCompact), backgroundColor: dayColumnColor(dayKey) }}
                        />
                    ),
                )}
            </TableRow>

            {isExpanded
                ? group.rows.map((row) => (
                      <TableRow key={row.rowKey} data-test={`production-overview.row-${row.rowKey}`}>
                          <TableCell sx={{ ...labelCellStyle, paddingLeft: 5 }}>
                              <Typography variant="body2" fontSize={isCompact ? '0.75rem' : undefined}>
                                  {translate(`production-overview:${row.labelKey}`)}
                              </Typography>
                          </TableCell>
                          {days.map((dayKey) => (
                              <CountCell
                                  key={dayKey}
                                  dayKey={dayKey}
                                  count={getRowCount(row, dayKey, countMode)}
                                  lateCount={
                                      dayKey === todayKey && row.lateToday
                                          ? pickCount(row.lateToday, countMode)
                                          : undefined
                                  }
                                  heatMaxCount={heatMaxCount}
                                  isCompact={isCompact}
                                  isNotYet={!row.hasFutureDays && isFutureDay(dayKey)}
                                  onClick={() => handleRowCellClick(row, dayKey)}
                                  onLateClick={() => handleLateTodayClick(row)}
                              />
                          ))}
                      </TableRow>
                  ))
                : undefined}
        </Fragment>
    );
};

export default OverviewGroupRow;
